import React, { useCallback, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import { fileUploadService } from '../../services/fileUploadService';
import { compressImage, formatFileSize, isImageFile } from '../../utils/imageCompression';
import { showToast } from '../../utils/toast';
import './RichTextEditor.css';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  topicId?: string; // For image uploads
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  placeholder = 'Write your content here...',
  readOnly = false,
  className = '',
  topicId
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit,
      Bold,
      Italic,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rich-text-image',
        },
      }),
      BulletList.configure({
        HTMLAttributes: {
          class: 'rich-text-bullet-list',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'rich-text-ordered-list',
        },
      }),
      ListItem,
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none rich-text-content',
        placeholder,
      },
    },
  });

  // Sync content prop with editor state
  useEffect(() => {
    if (editor && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [editor, content]);

  const setLink = useCallback(() => {
    if (!editor) return;
    
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // update link
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    
    const url = window.prompt('Image URL');

    if (url) {
      editor.chain().focus().setImage({ src: url }).run();
    }
  }, [editor]);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!editor || !topicId) return;

    // Check file type
    if (!isImageFile(file)) {
      showToast('Please select a valid image file', 'warning');
      return;
    }

    const maxSize = 1024 * 1024; // 1MB in bytes
    let fileToUpload = file;

    // Check if compression is needed
    if (file.size > maxSize) {
      const shouldCompress = window.confirm(
        `Image size is ${formatFileSize(file.size)}, which exceeds the 1MB limit. ` +
        'Would you like to compress it automatically? This will reduce the quality but keep the image.'
      );

      if (!shouldCompress) {
        return;
      }

      setUploadingImage(true);

      try {
        const compressionResult = await compressImage(file, {
          maxSizeBytes: maxSize,
          quality: 0.8,
          maxWidth: 1920,
          maxHeight: 1080
        });

        fileToUpload = compressionResult.file;

        // Still check if compressed size is acceptable
        if (fileToUpload.size > maxSize) {
          showToast(
            `Even after compression, the image is still ${formatFileSize(fileToUpload.size)}. ` +
            'Please try a smaller image or use an image editing tool to reduce the size.',
            'warning'
          );
          setUploadingImage(false);
          return;
        }

        // Inform user about compression
        const savedSpace = ((file.size - fileToUpload.size) / file.size * 100).toFixed(1);
        console.log(
          `Image compressed from ${formatFileSize(file.size)} to ${formatFileSize(fileToUpload.size)} ` +
          `(${savedSpace}% reduction)`
        );
      } catch (error) {
        console.error('Compression failed:', error);
        showToast('Failed to compress image. Please try a different image or reduce its size manually.', 'error');
        setUploadingImage(false);
        return;
      }
    } else {
      setUploadingImage(true);
    }

    try {
      const uploadResult = await fileUploadService.uploadForumAttachment(fileToUpload, topicId || undefined);
      editor.chain().focus().setImage({ 
        src: uploadResult.url,
        alt: file.name,
        title: file.name
      }).run();
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    } finally {
      setUploadingImage(false);
    }
  }, [editor, topicId]);

  const triggerImageUpload = useCallback(() => {
    if (!topicId) {
      // Fallback to URL input if no topicId
      addImage();
      return;
    }
    
    fileInputRef.current?.click();
  }, [topicId, addImage]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleImageUpload]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`rich-text-editor ${className}`}>
      {!readOnly && (
        <div className="rich-text-toolbar">
          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`}
              title="Bold"
            >
              <i className="bi bi-type-bold"></i>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`}
              title="Italic"
            >
              <i className="bi bi-type-italic"></i>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`}
              title="Underline"
            >
              <i className="bi bi-type-underline"></i>
            </button>
          </div>

          <div className="toolbar-group">
            <button
              type="button"
              onClick={setLink}
              className={`toolbar-btn ${editor.isActive('link') ? 'active' : ''}`}
              title="Add Link"
            >
              <i className="bi bi-link-45deg"></i>
            </button>
            <button
              type="button"
              onClick={triggerImageUpload}
              className="toolbar-btn"
              title={topicId ? "Upload Image (max 1MB)" : "Add Image URL"}
              disabled={uploadingImage}
            >
              {uploadingImage ? (
                <i className="bi bi-arrow-clockwise spin"></i>
              ) : (
                <i className="bi bi-image"></i>
              )}
            </button>
          </div>

          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
              title="Bullet List"
            >
              <i className="bi bi-list-ul"></i>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`}
              title="Numbered List"
            >
              <i className="bi bi-list-ol"></i>
            </button>
          </div>

          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={`toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`}
              title="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`}
              title="Heading 2"
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'active' : ''}`}
              title="Heading 3"
            >
              H3
            </button>
          </div>

          <div className="toolbar-group">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`toolbar-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
              title="Quote"
            >
              <i className="bi bi-quote"></i>
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={`toolbar-btn ${editor.isActive('code') ? 'active' : ''}`}
              title="Code"
            >
              <i className="bi bi-code"></i>
            </button>
          </div>
        </div>
      )}
      
      <div className="rich-text-content-wrapper">
        <EditorContent editor={editor} />
      </div>
      
      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default RichTextEditor;
